import { useEffect, useState } from "react";
import { getSeoConfig } from "@/api/homeApi";
import { buildPageMetadata } from "@/seo";
import type { PageMetadataContent, PageType, SiteMetaConfig } from "@/seo";
import {
  buildBreadcrumbLd as buildBreadcrumbJsonLd,
  serializeJsonLd,
  toAbsoluteUrl,
} from "@/seo/jsonld";
import { SITE_ORIGIN as SEO_SITE_ORIGIN, DEFAULT_OG_IMAGE } from "@/seo/constants";
import { markSeoInvoked } from "@/utils/prerenderSignal";

// 向后兼容导出：与 `src/seo/constants.ts` 取值逐字符一致，供既有调用方
// （`pages/cases/detail`、`pages/products/detail` 等）继续引用。
export const SITE_ORIGIN = SEO_SITE_ORIGIN;
export const DEFAULT_IMAGE = DEFAULT_OG_IMAGE;

// 默认值（fallback，站点级 SEO 数据源失败或超时时使用）。
// 保留独立于 `src/seo/metadata.ts` 的 FALLBACK_SITE_NAME，
// 是因为这两个函数在改造前就已导出且未见其他调用方依赖具体取值——
// 变更其内容属于不必要的行为改动，这里只把计算逻辑委托出去，取值维持原样。
const FALLBACK_SITE_NAME = "欧格登";
const FALLBACK_KEYWORDS = [
  "欧格登",
  "欧格登智能",
  "欧格登科技",
  "ogolden",
  "欧格登官网",
  "欧格登智能科技",
  "企业安全解决方案",
  "信息安全",
  "网络安全",
  "IT基础架构",
  "云信达",
  "等保三级",
  "零信任",
  "数据中心",
];
const FALLBACK_DESCRIPTION = "欧格登智能科技 - 企业级网络安全与IT基础架构综合解决方案服务商";

/** 站点级 SEO 数据源的等待上限（需求 2.17、11.14） */
const SEO_CONFIG_TIMEOUT_MS = 3000;

// 全局 SEO 配置缓存
let _seoCache: { siteTitle: string; siteKeywords: string; siteDescription: string } | null = null;
let _seoPromise: Promise<void> | null = null;

function loadSeoConfig(): Promise<void> {
  if (!_seoPromise) {
    _seoPromise = getSeoConfig()
      .then((res: any) => {
        if (res?.data) {
          _seoCache = {
            siteTitle: res.data.siteTitle || "",
            siteKeywords: res.data.siteKeywords || "",
            siteDescription: res.data.siteDescription || "",
          };
        }
      })
      .catch((err) => {
        // 请求失败：记录 URL、失败时间与失败原因（需求 2.17），随后由调用方
        // 用 `src/seo/*` 的内置兜底常量继续渲染，不阻塞页面其余区块。
        console.error(
          "[SEO] 站点级 SEO 数据源加载失败，使用 FALLBACK 常量渲染 Page_Metadata。",
          {
            url: typeof window !== "undefined" ? window.location.pathname : "",
            time: new Date().toISOString(),
            reason: err instanceof Error ? err.message : String(err),
          },
        );
      });
  }
  return _seoPromise;
}

// 启动时立即加载
loadSeoConfig();

/** 获取站点名称（优先使用后台配置） */
export function getSiteName(): string {
  return _seoCache?.siteTitle || FALLBACK_SITE_NAME;
}

/** 获取默认关键词（后台配置 + fallback 合并去重） */
export function getDefaultKeywords(): string[] {
  if (_seoCache?.siteKeywords) {
    const apiKeywords = _seoCache.siteKeywords.split(",").map((k) => k.trim()).filter(Boolean);
    return Array.from(new Set([...apiKeywords, ...FALLBACK_KEYWORDS]));
  }
  return FALLBACK_KEYWORDS;
}

/** 获取默认描述 */
export function getDefaultDescription(): string {
  return _seoCache?.siteDescription || FALLBACK_DESCRIPTION;
}

// 保留向后兼容的导出
export const SITE_NAME = FALLBACK_SITE_NAME;
export const DEFAULT_KEYWORDS = FALLBACK_KEYWORDS;

export interface SEOOptions {
  /** 页面标题（会自动拼接站点名） */
  title: string;
  /** 页面描述（建议 70-160 字） */
  description: string;
  /** 额外关键词，会与默认关键词合并去重 */
  keywords?: string[];
  /** 当前页面路径，例如 "/solutions"（用于 canonical / og:url） */
  path?: string;
  /** 社交分享图绝对 URL */
  image?: string;
  /** 是否禁止搜索引擎收录，默认 false */
  noindex?: boolean;
  /** 扩展结构化数据（JSON-LD） */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** og:type，默认 website，文章详情页建议传 article */
  ogType?: "website" | "article" | "product";
  /**
   * 页面类型，决定 `src/seo/*` 兜底文案与内容质量下限的取用来源。
   * 可选字段，缺省时按 `HOME` 处理——`HOME` 的预设文案覆盖面最广，
   * 且现有调用方（改造前未传该字段）都会得到与此前行为等价的非空兜底结果。
   */
  pageType?: PageType;
}

const JSONLD_TAG_ID = "seo-page-jsonld";

function upsertMeta(
  selector: string,
  attrName: "name" | "property",
  attrValue: string,
  content: string,
) {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function removeJsonLd() {
  document.querySelectorAll(`script[data-seo="${JSONLD_TAG_ID}"]`).forEach((n) => n.remove());
}

function injectJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  const list = Array.isArray(data) ? data : [data];
  list.forEach((obj) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-seo", JSONLD_TAG_ID);
    // 经 serializeJsonLd() 转义 `<`/`>`/`&`，与构建期预渲染共用同一套取值逻辑
    // （需求 11.1、11.13）。运行时用 script.text 赋值本身即安全，这里统一走
    // 同一函数只是为了两条路径（运行时 / 预渲染序列化）不出现两份实现。
    s.text = serializeJsonLd(obj);
    document.head.appendChild(s);
  });
}

/**
 * 为当前页面设置 SEO 标签：title / description / keywords / canonical
 * / og:* / twitter:* / robots / JSON-LD。
 * 每次 options 变化都会重新写入，页面切换时上一页的 JSON-LD 会被清理。
 *
 * title / description / keywords / canonical 的计算与 JSON-LD 构造全部委托给
 * `src/seo/*` 纯函数层（需求 11.1），保证运行时与构建期预渲染共用同一套取值逻辑。
 */
export function useSEO(options: SEOOptions) {
  const {
    title,
    description,
    keywords = [],
    path,
    image = DEFAULT_IMAGE,
    noindex = false,
    jsonLd,
    ogType = "website",
    pageType = "HOME",
  } = options;

  const [ready, setReady] = useState(!!_seoCache);

  useEffect(() => {
    if (_seoCache) return;

    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      // 3 秒未返回：记录 URL 与失败时间，随后回退 FALLBACK 常量继续渲染
      // （需求 2.17、11.14）。已输出的 Page_Metadata 不会被清空——下面的
      // setReady(true) 只会让计算 effect 用 `src/seo/*` 内置兜底常量重新计算，
      // buildPageMetadata() 恒返回非空、满足长度约束的完整结果。
      console.error("[SEO] 站点级 SEO 数据源 3 秒未返回，使用 FALLBACK 常量渲染 Page_Metadata。", {
        url: typeof window !== "undefined" ? window.location.pathname : "",
        time: new Date().toISOString(),
      });
      setReady(true);
    }, SEO_CONFIG_TIMEOUT_MS);

    loadSeoConfig().then(() => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      setReady(true);
    });

    return () => {
      settled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    // 预渲染就绪信号的两个条件之一：当前页面组件已至少调用过一次 useSEO()
    // （另一条件——在途请求数为 0 持续 300ms——由 src/api/request.ts 维护）。
    markSeoInvoked();

    const content: PageMetadataContent = {
      title,
      description,
      keywords,
      path,
      image,
      noindex,
      ogType,
    };
    const siteConfig: SiteMetaConfig = _seoCache
      ? {
          siteTitle: _seoCache.siteTitle,
          siteKeywords: _seoCache.siteKeywords,
          siteDescription: _seoCache.siteDescription,
        }
      : {};

    const metadata = buildPageMetadata(pageType, content, siteConfig);

    document.title = metadata.title;

    upsertMeta('meta[name="description"]', "name", "description", metadata.description);
    upsertMeta('meta[name="keywords"]', "name", "keywords", metadata.keywords);
    upsertMeta('meta[name="robots"]', "name", "robots", metadata.robots);

    upsertMeta('meta[property="og:title"]', "property", "og:title", metadata.og["og:title"]);
    upsertMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      metadata.og["og:description"],
    );
    upsertMeta('meta[property="og:url"]', "property", "og:url", metadata.og["og:url"]);
    upsertMeta('meta[property="og:image"]', "property", "og:image", metadata.og["og:image"]);
    upsertMeta('meta[property="og:type"]', "property", "og:type", metadata.og["og:type"]);
    upsertMeta(
      'meta[property="og:site_name"]',
      "property",
      "og:site_name",
      metadata.og["og:site_name"],
    );
    upsertMeta('meta[property="og:locale"]', "property", "og:locale", metadata.og["og:locale"]);

    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", metadata.twitter["twitter:card"]);
    upsertMeta(
      'meta[name="twitter:title"]',
      "name",
      "twitter:title",
      metadata.twitter["twitter:title"],
    );
    upsertMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      metadata.twitter["twitter:description"],
    );
    upsertMeta(
      'meta[name="twitter:image"]',
      "name",
      "twitter:image",
      metadata.twitter["twitter:image"],
    );

    upsertCanonical(metadata.canonical);

    removeJsonLd();
    if (jsonLd) injectJsonLd(jsonLd);

    return () => {
      // 页面卸载时移除本页 JSON-LD，避免累积
      removeJsonLd();
    };
  }, [
    title,
    description,
    keywords.join("|"),
    path,
    image,
    noindex,
    ogType,
    pageType,
    JSON.stringify(jsonLd),
    ready,
  ]);
}

/**
 * 生成面包屑 JSON-LD 结构化数据。
 *
 * 签名与入参结构保持不变（需求 11.1）：仍接受「首页 → … → 当前页」的
 * `{ name, path }[]` 数组，不要求调用方额外传 canonical。内部委托给
 * `src/seo/jsonld/page.ts` 的新版 `buildBreadcrumbLd(items, canonicalUrl)`——
 * 那个版本额外要求一个 `canonicalUrl` 参数，这里用 `toAbsoluteUrl(末元素.path)`
 * 派生出等价取值后传入，与旧实现里末元素 `item` 的计算方式（`SITE_ORIGIN` + path）
 * 结果一致。
 */
export function buildBreadcrumbLd(items: { name: string; path: string }[]) {
  const last = items.length > 0 ? items[items.length - 1] : undefined;
  const canonicalUrl = toAbsoluteUrl(last?.path);
  return buildBreadcrumbJsonLd(items, canonicalUrl);
}
